import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  SlidersHorizontal,
  RotateCcw,
  Coins,
  Users,
  Trophy,
  Banknote,
  Receipt,
  Percent,
  Wallet,
  TrendingUp,
} from 'lucide-react';
import { formatCurrency, formatNumber, formatRatio } from '../lib/utils';
import { useAggregatedInsights } from '../hooks/useAggregatedInsights';
import { useAnnotationIndex, useDistinctTags, annotationKey } from '../hooks/useAdAnnotations';
import DateRangeSelector from '../components/dashboard/DateRangeSelector';
import AccountSelector from '../components/dashboard/AccountSelector';
import TagsCell from '../components/dashboard/TagsCell';
import NotesCell from '../components/dashboard/NotesCell';
import TagFilterDropdown, { UNTAGGED } from '../components/dashboard/TagFilterDropdown';
import { useDateRangeStore } from '../store/dateRangeStore';
import { useAccountStore } from '../store/accountStore';
import type { AdAnnotation } from '../api/adAnnotations';

function isStatusActive(status?: string): boolean {
  if (!status) return false;
  const s = status.toUpperCase();
  return s === 'ACTIVE' || s === 'ENABLE';
}

function shiftISODate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function getPriorRange(startDate: string, endDate: string): { startDate: string; endDate: string } {
  const start = new Date(`${startDate}T00:00:00Z`).getTime();
  const end = new Date(`${endDate}T00:00:00Z`).getTime();
  const days = Math.round((end - start) / 86_400_000) + 1; // inclusive
  return {
    endDate: shiftISODate(startDate, -1),
    startDate: shiftISODate(startDate, -days),
  };
}

interface Insight {
  id: string;
  accountId: string;
  name: string;
  spend: number;
  leads: number;
  dealsWon: number;
  conversionRate: number | null;
  revenue: number;
  averageDeal: number | null;
  cpl: number | null;
  costPerDeal: number | null;
  roas: number | null;
  status?: string;
  isActive: number; // 1 = active, 0 = inactive — used for sorting
  tags: string[];
  noteCount: number;
  annotation?: AdAnnotation;
}

type SortField =
  | 'name'
  | 'isActive'
  | 'spend'
  | 'leads'
  | 'dealsWon'
  | 'conversionRate'
  | 'revenue'
  | 'averageDeal'
  | 'cpl'
  | 'costPerDeal'
  | 'roas';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface FilterRange {
  min: string;
  max: string;
}

interface FilterState {
  spend: FilterRange;
  leads: FilterRange;
  dealsWon: FilterRange;
  conversionRate: FilterRange;
  revenue: FilterRange;
  averageDeal: FilterRange;
  cpl: FilterRange;
  costPerDeal: FilterRange;
  roas: FilterRange;
}

const initialFilters: FilterState = {
  spend: { min: '', max: '' },
  leads: { min: '', max: '' },
  dealsWon: { min: '', max: '' },
  conversionRate: { min: '', max: '' },
  revenue: { min: '', max: '' },
  averageDeal: { min: '', max: '' },
  cpl: { min: '', max: '' },
  costPerDeal: { min: '', max: '' },
  roas: { min: '', max: '' },
};

export default function Insights() {
  const { t } = useTranslation();
  const { dateRange, setDateRange } = useDateRangeStore();
  const { selectedAccountId, setSelectedAccountId } = useAccountStore();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'spend', direction: 'desc' });
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [tempFilters, setTempFilters] = useState<FilterState>(initialFilters);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [statusFilter, setStatusFilter] = useState<{ active: boolean; inactive: boolean }>({
    active: true,
    inactive: true,
  });
  const [tempStatusFilter, setTempStatusFilter] = useState<{ active: boolean; inactive: boolean }>({
    active: true,
    inactive: true,
  });
  // Selected tag-filter buckets. Default = every tag + UNTAGGED selected
  // (i.e. no restriction). New tags discovered later are auto-added to the
  // selection so the default stays "show everything". Tags the user
  // explicitly unchecks stay unchecked.
  const [selectedTagFilters, setSelectedTagFilters] = useState<Set<string>>(
    () => new Set([UNTAGGED])
  );
  const seenTagsRef = useRef<Set<string>>(new Set([UNTAGGED]));

  const annotationIndex = useAnnotationIndex();
  const { data: allDistinctTags } = useDistinctTags();

  useEffect(() => {
    if (!allDistinctTags) return;
    const fresh: string[] = [];
    for (const tag of allDistinctTags) {
      if (!seenTagsRef.current.has(tag)) {
        seenTagsRef.current.add(tag);
        fresh.push(tag);
      }
    }
    if (fresh.length > 0) {
      setSelectedTagFilters((prev) => {
        const next = new Set(prev);
        for (const f of fresh) next.add(f);
        return next;
      });
    }
  }, [allDistinctTags]);

  const { data, isLoading } = useAggregatedInsights({
    selectedAccountId,
    dateRange: 'custom',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Prior period: same number of days, immediately before the selected range.
  // Used to compute ROAS change per ad.
  const priorRange = useMemo(
    () => getPriorRange(dateRange.startDate, dateRange.endDate),
    [dateRange.startDate, dateRange.endDate]
  );
  const { data: priorData } = useAggregatedInsights({
    selectedAccountId,
    dateRange: 'custom',
    startDate: priorRange.startDate,
    endDate: priorRange.endDate,
  });

  // Lookup map: ad id -> prior-period ROAS (null when unavailable).
  const priorRoasById = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const item of priorData ?? []) {
      map.set(item.id, item.roas);
    }
    return map;
  }, [priorData]);

  // Transform raw API rows: add conversionRate, averageDeal, isActive flag,
  // and merge in per-ad annotation (tags + notes).
  const insights: Insight[] = useMemo(() => {
    if (!data) return [];
    return data.map((item) => {
      const accountId = (item as { accountId?: string }).accountId ?? selectedAccountId;
      const ann = annotationIndex.get(annotationKey(accountId, item.id));
      return {
        ...item,
        accountId,
        conversionRate: item.leads > 0 ? (item.dealsWon / item.leads) * 100 : null,
        averageDeal: item.dealsWon > 0 ? item.revenue / item.dealsWon : null,
        status: item.status,
        isActive: isStatusActive(item.status) ? 1 : 0,
        tags: ann?.tags ?? [],
        noteCount: ann?.notes.length ?? 0,
        annotation: ann,
      };
    });
  }, [data, annotationIndex, selectedAccountId]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'desc' ? (
      <ChevronDown className="h-4 w-4 text-primary" />
    ) : (
      <ChevronUp className="h-4 w-4 text-primary" />
    );
  };

  const statusFilterActive =
    !(statusFilter.active && statusFilter.inactive); // restrictive when not both checked

  // Filter button badge: counts numeric range filters + status filter. The
  // tag strip sits inline under the search bar, so it doesn't count here.
  const hasActiveFilters = useMemo(() => {
    return (
      Object.values(filters).some((f) => f.min !== '' || f.max !== '') ||
      statusFilterActive
    );
  }, [filters, statusFilterActive]);

  const activeFilterCount = useMemo(() => {
    return (
      Object.values(filters).filter((f) => f.min !== '' || f.max !== '').length +
      (statusFilterActive ? 1 : 0)
    );
  }, [filters, statusFilterActive]);

  // Handle filter popup open
  const openFilterPopup = () => {
    setTempFilters(filters);
    setTempStatusFilter(statusFilter);
    setShowFilterPopup(true);
  };

  // Handle filter apply
  const applyFilters = () => {
    setFilters(tempFilters);
    // Guard: never let user save "both off" — degenerate empty result. Force
    // back to both-on if they uncheck both.
    const safeStatus = tempStatusFilter.active || tempStatusFilter.inactive
      ? tempStatusFilter
      : { active: true, inactive: true };
    setStatusFilter(safeStatus);
    setShowFilterPopup(false);
  };

  // Handle filter clear (numeric filters + status — tag chips live outside)
  const clearFilters = () => {
    setFilters(initialFilters);
    setTempFilters(initialFilters);
    setStatusFilter({ active: true, inactive: true });
    setTempStatusFilter({ active: true, inactive: true });
    setShowFilterPopup(false);
  };

  // Update temp filter value
  const updateTempFilter = (field: keyof FilterState, type: 'min' | 'max', value: string) => {
    setTempFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], [type]: value },
    }));
  };

  // Check if a value passes a filter
  const passesFilter = (value: number | null, filter: FilterRange): boolean => {
    if (filter.min === '' && filter.max === '') return true;
    if (value === null) return filter.min === '' && filter.max === '';

    const minVal = filter.min !== '' ? parseFloat(filter.min) : null;
    const maxVal = filter.max !== '' ? parseFloat(filter.max) : null;

    if (minVal !== null && value < minVal) return false;
    if (maxVal !== null && value > maxVal) return false;
    return true;
  };

  // Filter and sort insights
  const filteredAndSortedInsights = useMemo(() => {
    let result = [...insights];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((insight) =>
        insight.name.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilterActive) {
      result = result.filter((insight) =>
        insight.isActive ? statusFilter.active : statusFilter.inactive
      );
    }

    // Apply numeric filters
    result = result.filter((insight) => {
      if (!passesFilter(insight.spend, filters.spend)) return false;
      if (!passesFilter(insight.leads, filters.leads)) return false;
      if (!passesFilter(insight.dealsWon, filters.dealsWon)) return false;
      if (!passesFilter(insight.conversionRate, filters.conversionRate)) return false;
      if (!passesFilter(insight.revenue, filters.revenue)) return false;
      if (!passesFilter(insight.averageDeal, filters.averageDeal)) return false;
      if (!passesFilter(insight.cpl, filters.cpl)) return false;
      if (!passesFilter(insight.costPerDeal, filters.costPerDeal)) return false;
      if (!passesFilter(insight.roas, filters.roas)) return false;
      return true;
    });

    // Apply tag filter. Strict semantics:
    //   - An ad with tags passes iff any of its tags is in the selected set.
    //   - An ad with no tags passes iff UNTAGGED is selected.
    // The "no filter active" shortcut: when *every* known bucket
    // (UNTAGGED + each distinct tag) is selected, skip filtering entirely.
    const totalBuckets = (allDistinctTags?.length ?? 0) + 1; // +1 for UNTAGGED
    const allSelected = selectedTagFilters.size >= totalBuckets;
    if (!allSelected) {
      const wantedLower = new Set<string>();
      let untaggedSelected = false;
      for (const t of selectedTagFilters) {
        if (t === UNTAGGED) untaggedSelected = true;
        else wantedLower.add(t.toLowerCase());
      }
      result = result.filter((insight) => {
        if (insight.tags.length === 0) return untaggedSelected;
        return insight.tags.some((t) => wantedLower.has(t.toLowerCase()));
      });
    }

    // Sort
    result.sort((a, b) => {
      const { field, direction } = sortConfig;
      const multiplier = direction === 'desc' ? -1 : 1;

      // Handle string field (name)
      if (field === 'name') {
        return multiplier * a.name.localeCompare(b.name, 'he');
      }

      // Handle numeric fields (including nulls)
      const aVal = a[field];
      const bVal = b[field];

      // Null values go to the end regardless of sort direction
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;

      return multiplier * (aVal - bVal);
    });

    return result;
  }, [insights, searchQuery, sortConfig, filters, selectedTagFilters, allDistinctTags, statusFilter, statusFilterActive]);

  // Sortable column header component
  const SortableHeader = ({
    field,
    align = 'end',
    children,
  }: {
    field: SortField;
    align?: 'start' | 'end';
    children: React.ReactNode;
  }) => (
    <th
      className={`px-6 py-3 text-${align} text-xs font-medium uppercase text-gray-500 cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'end' ? 'justify-end' : 'justify-start'}`}>
        <span>{children}</span>
        {getSortIcon(field)}
      </div>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('insights.title')}</h1>

        <div className="flex items-center gap-4">
          {/* Account Selector */}
          <AccountSelector
            value={selectedAccountId}
            onChange={setSelectedAccountId}
          />
          {/* Filter Button */}
          <button
            onClick={openFilterPopup}
            className={`relative flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition-colors ${
              hasActiveFilters
                ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          {/* Date Range */}
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('common.search')}
          className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Tag filter dropdown — multi-select with checklist; default: all selected. */}
      <TagFilterDropdown
        allTags={allDistinctTags ?? []}
        selected={selectedTagFilters}
        onChange={setSelectedTagFilters}
      />

      {/* Table */}
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <SortableHeader field="name" align="start">
                  {t('insights.ad')}
                </SortableHeader>
                <SortableHeader field="isActive" align="start">
                  {t('insights.status')}
                </SortableHeader>
                <SortableHeader field="spend">
                  {t('kpis.spend')}
                </SortableHeader>
                <SortableHeader field="leads">
                  {t('kpis.leads')}
                </SortableHeader>
                <SortableHeader field="dealsWon">
                  {t('kpis.deals')}
                </SortableHeader>
                <SortableHeader field="conversionRate">
                  {t('kpis.conversionRate')}
                </SortableHeader>
                <SortableHeader field="revenue">
                  {t('kpis.revenue')}
                </SortableHeader>
                <SortableHeader field="averageDeal">
                  {t('kpis.averageDeal')}
                </SortableHeader>
                <SortableHeader field="cpl">
                  {t('kpis.cpl')}
                </SortableHeader>
                <SortableHeader field="costPerDeal">
                  {t('kpis.costPerDeal')}
                </SortableHeader>
                <SortableHeader field="roas">
                  {t('kpis.roas')}
                </SortableHeader>
                <th className="px-6 py-3 text-start text-xs font-medium uppercase text-gray-500 select-none whitespace-nowrap">
                  {t('insights.tags')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium uppercase text-gray-500 select-none whitespace-nowrap">
                  {t('insights.notes')}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={13} className="px-6 py-8 text-center text-gray-500">
                    {t('common.loading')}...
                  </td>
                </tr>
              ) : filteredAndSortedInsights.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-6 py-8 text-center text-gray-500">
                    {searchQuery ? t('common.noResults') : t('common.noData')}
                  </td>
                </tr>
              ) : (
                filteredAndSortedInsights.map((insight) => (
                  <tr key={`${insight.accountId}:${insight.id}`} className="border-b hover:bg-gray-50">
                    <td className="min-w-[220px] px-6 py-4 font-medium text-gray-900">
                      {insight.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${
                            insight.isActive ? 'bg-green-500' : 'bg-red-500'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="text-gray-600">
                          {insight.isActive
                            ? t('insights.statusActive')
                            : t('insights.statusInactive')}
                        </span>
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {formatCurrency(insight.spend)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {formatNumber(insight.leads)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {formatNumber(insight.dealsWon)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {insight.conversionRate !== null ? `${insight.conversionRate.toFixed(1)}%` : '-'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {formatCurrency(insight.revenue)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {insight.averageDeal ? formatCurrency(insight.averageDeal) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {insight.cpl ? formatCurrency(insight.cpl) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end text-gray-600">
                      {insight.costPerDeal ? formatCurrency(insight.costPerDeal) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-end">
                      {insight.roas !== null ? (
                        <div className="flex items-center justify-end gap-2">
                          {(() => {
                            const prev = priorRoasById.get(insight.id);
                            if (prev === undefined || prev === null || prev === 0) return null;
                            const change = ((insight.roas - prev) / prev) * 100;
                            const rounded = Math.round(change);
                            if (rounded === 0) return null;
                            const isUp = rounded > 0;
                            const Icon = isUp ? ArrowUp : ArrowDown;
                            return (
                              <span
                                className={`inline-flex items-center text-xs font-medium ${
                                  isUp ? 'text-green-600' : 'text-red-600'
                                }`}
                                title={`vs. ${priorRange.startDate} → ${priorRange.endDate}: ${formatRatio(prev)}`}
                              >
                                <Icon className="h-3 w-3" />
                                {Math.abs(rounded)}%
                              </span>
                            );
                          })()}
                          <span
                            className={`font-medium ${
                              insight.roas >= 3
                                ? 'text-green-600'
                                : insight.roas >= 1
                                ? 'text-yellow-600'
                                : 'text-red-600'
                            }`}
                          >
                            {formatRatio(insight.roas)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="min-w-[140px] px-6 py-4 align-top">
                      <TagsCell
                        accountId={insight.accountId}
                        adId={insight.id}
                        tags={insight.tags}
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 align-top">
                      <NotesCell
                        accountId={insight.accountId}
                        adId={insight.id}
                        adName={insight.name}
                        annotation={insight.annotation}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filter Popup Modal */}
      {showFilterPopup && (() => {
        // Render helper for one filter row (icon · label · Min · – · Max).
        const renderFilterRow = (
          field: keyof FilterState,
          Icon: typeof Coins,
          label: string,
          opts: { prefix?: string; suffix?: string; step?: string } = {}
        ) => {
          const { prefix, suffix, step } = opts;
          const inputBase =
            'h-9 w-full rounded-md border border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15';
          const paddingX = `${prefix ? 'ps-6 pe-2' : 'px-2'} ${suffix ? 'pe-6' : ''}`;
          return (
            <div className="flex items-center gap-3 border-b border-gray-200 py-2.5 last:border-b-0">
              <div className="flex flex-1 items-center gap-2 text-sm text-gray-800">
                <Icon className="h-4 w-4 text-gray-500" />
                <span>{label}</span>
              </div>
              <div className="relative w-[110px]">
                {prefix && (
                  <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs text-gray-400">
                    {prefix}
                  </span>
                )}
                {suffix && (
                  <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">
                    {suffix}
                  </span>
                )}
                <input
                  type="number"
                  step={step}
                  value={tempFilters[field].min}
                  onChange={(e) => updateTempFilter(field, 'min', e.target.value)}
                  placeholder={t('filter.min')}
                  className={`${inputBase} ${paddingX}`}
                />
              </div>
              <span className="text-gray-400">–</span>
              <div className="relative w-[110px]">
                {prefix && (
                  <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs text-gray-400">
                    {prefix}
                  </span>
                )}
                {suffix && (
                  <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">
                    {suffix}
                  </span>
                )}
                <input
                  type="number"
                  step={step}
                  value={tempFilters[field].max}
                  onChange={(e) => updateTempFilter(field, 'max', e.target.value)}
                  placeholder={t('filter.max')}
                  className={`${inputBase} ${paddingX}`}
                />
              </div>
            </div>
          );
        };

        // Count filters active in the *draft* (mirrors header counter).
        let draftCount = 0;
        for (const f of Object.values(tempFilters)) {
          if (f.min !== '' || f.max !== '') draftCount++;
        }
        if (!(tempStatusFilter.active && tempStatusFilter.inactive)) draftCount++;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-black/10 bg-white shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-black/10 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-primary">
                    <SlidersHorizontal className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-base font-semibold text-gray-900">
                      {t('filter.title')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('filter.filtersActive', { count: draftCount })}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowFilterPopup(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-black/10 text-gray-500 hover:bg-gray-50"
                  aria-label={t('common.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5">
                {/* Status bar */}
                <div className="mb-5 flex items-center justify-between gap-3 rounded-md bg-gray-50 px-4 py-3">
                  <span className="text-sm font-semibold text-gray-600">
                    {t('insights.status')}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setTempStatusFilter((s) => ({ ...s, active: !s.active }))
                      }
                      className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-sm transition ${
                        tempStatusFilter.active
                          ? 'border-primary/30 bg-blue-50 text-primary'
                          : 'border-transparent bg-transparent text-gray-400 opacity-70'
                      }`}
                      aria-pressed={tempStatusFilter.active}
                    >
                      <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                      {t('insights.statusActive')}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setTempStatusFilter((s) => ({ ...s, inactive: !s.inactive }))
                      }
                      className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-sm transition ${
                        tempStatusFilter.inactive
                          ? 'border-primary/30 bg-blue-50 text-primary'
                          : 'border-transparent bg-transparent text-gray-400 opacity-70'
                      }`}
                      aria-pressed={tempStatusFilter.inactive}
                    >
                      <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
                      {t('insights.statusInactive')}
                    </button>
                  </div>
                </div>

                {/* Group: Volume */}
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t('filter.groupVolume')}
                </p>
                <div>
                  {renderFilterRow('spend', Coins, t('kpis.spend'), { prefix: '₪' })}
                  {renderFilterRow('leads', Users, t('kpis.leads'))}
                  {renderFilterRow('dealsWon', Trophy, t('kpis.deals'))}
                </div>

                {/* Group: Revenue & efficiency */}
                <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t('filter.groupRevenue')}
                </p>
                <div>
                  {renderFilterRow('revenue', Banknote, t('kpis.revenue'), { prefix: '₪' })}
                  {renderFilterRow('averageDeal', Receipt, t('kpis.averageDeal'), { prefix: '₪' })}
                  {renderFilterRow('conversionRate', Percent, t('kpis.conversionRate'), { suffix: '%', step: '0.1' })}
                </div>

                {/* Group: Cost & return */}
                <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t('filter.groupCost')}
                </p>
                <div>
                  {renderFilterRow('cpl', Users, t('kpis.cpl'), { prefix: '₪' })}
                  {renderFilterRow('costPerDeal', Wallet, t('kpis.costPerDeal'), { prefix: '₪' })}
                  {renderFilterRow('roas', TrendingUp, t('kpis.roas'), { suffix: '×', step: '0.1' })}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 border-t border-black/10 bg-gray-50 px-6 py-3">
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 rounded-md border border-black/10 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <RotateCcw className="h-4 w-4" />
                  {t('filter.clearAll')}
                </button>
                <button
                  onClick={applyFilters}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                >
                  <Filter className="h-4 w-4" />
                  {t('filter.applyFilters')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
